import { stripDoubleSpaces } from 'bpm/shared/utils/inputUtils'
import { v4 as uuidv4 } from 'uuid'
import {
    ERROR_DUPLICATE_HEADER,
    ERROR_EMPTY_PROPERTY,
    ERROR_EMPTY_PROPERTYNAME,
} from '../constants'
import {
    NonMSAction,
    NonMSPropertyType,
    NonMSState,
    NonMSSubPropertyType,
    batchNonMSActions,
    createNonMSSubProperty,
    setGroupPostfix,
    setGroupPrefix,
    setNonMSEditProperty,
    setNonMSEditPropertyID,
    setNonMSErrors,
    setNonMSMode,
    setNonMSOptionActive,
    setNonMSOptions,
    setNonMSPristine,
    setNonMSProperties,
    setNonMSPropertyName,
    setNonMSSubProperties,
    setNonMSSubProperty,
    setNonMSSubPropertyName,
    setPrefixEditable,
    sortNonMSOptions,
} from './nonMSTypes'
export const generateNewOptionGroups = () => ({
    groupId: uuidv4(),
    prefix: '',
    postfix: '',
    options: [],
})

const generateNewSubProperty = (): NonMSSubPropertyType => ({
    subPropertyName: '',
    propertyId: uuidv4(),
    optionGroups: [
        {
            groupId: uuidv4(),
            prefix: '',
            postfix: '',
            options: [],
        },
    ],
    isPremiumPlus: true,
    supportedOptionIds: [''],
})

const generateNewProperty = (): NonMSPropertyType => ({
    propertyName: '',
    dummyPropertyId: uuidv4(),
    subProperties: [generateNewSubProperty()],
})

export const DEFAULT_NONMS_STATE: NonMSState = {
    properties: [],
    editableProperty: generateNewProperty(),
    editablePropertyId: '',
    mode: '',
    errors: {},
    pristineProps: [],
    optionActive: '',
    prefixEditable: [],
}

export const NON_MS_ACTIONS = {
    setMode: 'SET_MODE',
    setErrors: 'SET_ERRORS',
    resetErrors: 'RESET_ERRORS',
    setEditProperty: 'SET_EDIT_PROPERTY',
    setEditPropertyID: 'SET_EDIT_PROPERTY_ID',
    setPropertyName: 'SET_PROPERTY_NAME',
    setProperties: 'SET_PROPERTIES',
    setSubPropertyName: 'SET_SUBPROPERTY_NAME',
    setSubProperties: 'SET_SUBPROPERTIES',
    setSubProperty: 'SET_SUBPROPERTY',
    setPremiumOptions: 'SET_OPTIONS',
    createNewProperty: 'CREATE_NEW_PROPERTY',
    insertProperty: 'INSERT_PROPERTY',
    insertOptionGroup: 'INSERT_OPTION_GROUP',
    createNewSubProperty: 'CREATE_NEW_SUBPROPERTY',
    sortSetOptions: 'SORT_SET_OPTIONS',
    setPristineNonMS: 'SET_PRISTINE_NONMS',
    setOptionActive: 'SET_OPTION_ACTIVE',
    batchNonMSActions: 'BATCH_NONMS_ACTIONS',
    setPrefixEditable: 'SET_PREFIX_EDITABLE',
    setGroupPrefix: 'SET_GROUP_PREFIX',
    setGroupPostfix: 'SET_GROUP_POSTFIX',
    setOptionGroup: 'SET_OPTION_GROUP',
}

const purgeEmpty = (
    editableProperty: NonMSPropertyType,
    properties: Array<NonMSPropertyType>
) => {
    const hasEmptyPropertyNameWithOptions = editableProperty.subProperties.some(
        (subProperty) =>
            !subProperty.subPropertyName &&
            !!subProperty.optionGroups.some(
                (optGrp) => optGrp.options?.length || optGrp.prefix
            )
    )

    const purgedSubProperties = editableProperty.subProperties.filter(
        (subProperty) => !!subProperty.subPropertyName
    )

    if (
        properties.some(
            (property) =>
                property.propertyName === editableProperty.propertyName &&
                property.dummyPropertyId !== editableProperty.dummyPropertyId
        )
    ) {
        return {
            error: {
                propertyName: {
                    error: ERROR_DUPLICATE_HEADER,
                    value: editableProperty.propertyName,
                },
            },
        }
    }

    if (hasEmptyPropertyNameWithOptions) {
        return {
            error: {
                subPropertyName: { error: ERROR_EMPTY_PROPERTYNAME, value: '' },
            },
        }
    }

    if (!purgedSubProperties.length) {
        return {
            error: {
                subPropertyName: { error: ERROR_EMPTY_PROPERTY, value: '' },
            },
        }
    }

    return {
        propertyInsert: {
            ...editableProperty,
            subProperties: [
                ...editableProperty.subProperties.filter(
                    (subProperty) => !!subProperty.subPropertyName
                ),
            ],
        },
    }
}

const setNonMSModeReducer = (state: NonMSState, action: setNonMSMode) => ({
    ...state,
    mode: action.payload,
})

const setNonMSErrorReducer = (state: NonMSState, action: setNonMSErrors) => ({
    ...state,
    errors: { ...state.errors, ...action.payload },
})

const resetNonMSErrorReducer = (state: NonMSState) => ({
    ...state,
    errors: {},
})

const createNewPropertyReducer = (state: NonMSState) => ({
    ...state,
    editableProperty: generateNewProperty(),
})

const setEditPropertyReducer = (
    state: NonMSState,
    action: setNonMSEditProperty
) => ({
    ...state,
    editableProperty: action.payload,
})

const setEditPropertyID = (
    state: NonMSState,
    action: setNonMSEditPropertyID
) => ({
    ...state,
    editablePropertyId: action.payload,
    editableProperty: state.properties[action.payload as number],
})

export const insertToIndex = (
    varArray: any[],
    entryIndex: number,
    entry: any,
    replace?: boolean
) => [
    ...(varArray.slice(0, replace ? entryIndex : entryIndex + 1) ?? null),
    entry,
    ...(varArray.slice(entryIndex + 1, varArray.length) ?? null),
]

const insertPropertyReducer = (state: NonMSState) => {
    if (
        Object.values(state.errors).some((value) =>
            typeof value === 'string'
                ? !!value
                : Object.values(value as Record<string, any>).some(
                      (prop) => !!prop
                  )
        )
    ) {
        return state
    }

    const { propertyInsert, error } = purgeEmpty(
        state.editableProperty,
        state.properties
    )

    if (!propertyInsert && !!error) {
        return {
            ...state,
            errors: { ...state.errors, ...error },
        }
    }

    return {
        ...state,
        editableProperty: generateNewProperty(),
        properties:
            typeof state.editablePropertyId === 'number'
                ? insertToIndex(
                      state.properties,
                      state.editablePropertyId,
                      propertyInsert,
                      true
                  )
                : [propertyInsert, ...state.properties],
        mode: '',
        errors: {},
    }
}

const setPropertiesReducer = (
    state: NonMSState,
    action: setNonMSProperties
) => ({
    ...state,
    properties: action.payload,
})

const createNewSubPropertyReducer = (
    state: NonMSState,
    action: createNonMSSubProperty
) => {
    return {
        ...state,
        editableProperty: {
            ...state.editableProperty,
            subProperties: insertToIndex(
                state.editableProperty.subProperties,
                action.payload,
                generateNewSubProperty()
            ),
        },
    }
}

const setSubPropertiesReducer = (
    state: NonMSState,
    action: setNonMSSubProperties
) => ({
    ...state,
    editableProperty: {
        ...state.editableProperty,
        subProperties: action.payload,
    },
})

const setSubPropertyReducer = (
    state: NonMSState,
    { payload: { subProperty, subPropertyIndex } }: setNonMSSubProperty
) => ({
    ...state,
    editableProperty: {
        ...state.editableProperty,
        subProperties: insertToIndex(
            state.editableProperty.subProperties,
            subPropertyIndex,
            subProperty,
            true
        ),
    },
})

const setPropertyNameReducer = (
    state: NonMSState,
    action: setNonMSPropertyName
) => ({
    ...state,
    editableProperty: {
        ...state.editableProperty,
        propertyName: stripDoubleSpaces(action.payload),
    },
})

const setSubPropertyNameReducer = (
    state: NonMSState,
    action: setNonMSSubPropertyName
) => {
    const { subPropsIndex: index, subPropertyName } = action.payload

    return {
        ...state,
        editableProperty: {
            ...state.editableProperty,
            subProperties: insertToIndex(
                state.editableProperty.subProperties,
                index,
                {
                    ...state.editableProperty.subProperties[index],
                    subPropertyName: stripDoubleSpaces(subPropertyName),
                },
                true
            ),
        },
        errors: { subPropertyName: null },
    }
}

const setOptionsReducer = (state: NonMSState, action: setNonMSOptions) => {
    const {
        subPropIndex,
        subPropertyOptions,
        selectedOptions,
        groupIndex = 0,
    } = action.payload
    let errors = state.errors

    const emptyOptions = subPropertyOptions.length === 0

    if (
        emptyOptions &&
        (state.errors.subPropertyName as Record<string, string>)?.error ===
            ERROR_EMPTY_PROPERTYNAME
    ) {
        errors = { ...errors, subPropertyName: null }
    }

    return {
        ...state,
        editableProperty: {
            ...state.editableProperty,
            subProperties: insertToIndex(
                state.editableProperty.subProperties,
                subPropIndex,
                {
                    ...state.editableProperty.subProperties[subPropIndex],
                    optionGroups: insertToIndex(
                        state.editableProperty.subProperties[subPropIndex]
                            .optionGroups,
                        groupIndex,
                        {
                            ...state.editableProperty.subProperties[
                                subPropIndex
                            ].optionGroups[groupIndex],
                            options: subPropertyOptions,
                        },
                        true
                    ),
                    supportedOptionIds: selectedOptions,
                },
                true
            ),
        },
        errors,
    }
}

const sortOptionsReducer = (state: NonMSState, action: sortNonMSOptions) => {
    const {
        propIndex,
        subPropIndex,
        subPropertyOptions,
        selectedOptions,
        optionGroupIndex,
    } = action.payload

    return {
        ...state,
        properties: insertToIndex(
            state.properties,
            propIndex,
            {
                ...state.properties[propIndex],
                subProperties: insertToIndex(
                    state.properties[propIndex].subProperties,
                    subPropIndex,
                    {
                        ...state.properties[propIndex].subProperties[
                            subPropIndex
                        ],
                        optionGroups: insertToIndex(
                            state.properties[propIndex].subProperties[
                                subPropIndex
                            ]?.optionGroups,
                            optionGroupIndex,
                            {
                                ...state.properties[propIndex].subProperties[
                                    subPropIndex
                                ]?.optionGroups[optionGroupIndex],
                                options: subPropertyOptions,
                            },
                            true
                        ),

                        supportedOptionIds: selectedOptions,
                    },
                    true
                ),
            },
            true
        ),
    }
}

const setPristineNonMSReducer = (
    state: NonMSState,
    action: setNonMSPristine
) => ({
    ...state,
    pristineProps: action.payload,
    properties: action.payload,
})

const setOptionActiveReducer = (
    state: NonMSState,
    action: setNonMSOptionActive
) => ({
    ...state,
    optionActive: action.payload,
})

const setPrefixEditableReducer = (
    state: NonMSState,
    action: setPrefixEditable
) => ({
    ...state,
    prefixEditable: action.payload,
})

const setGroupPrefixReducer = (
    state: NonMSState,
    { payload: { subPropIndex, optionGroupIndex, prefixText } }: setGroupPrefix
) => {
    return {
        ...state,
        editableProperty: {
            ...state.editableProperty,
            subProperties: insertToIndex(
                state.editableProperty.subProperties,
                subPropIndex,
                {
                    ...state.editableProperty.subProperties[subPropIndex],
                    optionGroups: insertToIndex(
                        state.editableProperty.subProperties[subPropIndex]
                            .optionGroups,
                        optionGroupIndex,
                        {
                            ...state.editableProperty.subProperties[
                                subPropIndex
                            ].optionGroups[optionGroupIndex],
                            prefix: stripDoubleSpaces(prefixText),
                        },
                        true
                    ),
                },
                true
            ),
        },
    }
}

const setGroupPostfixReducer = (
    state: NonMSState,
    {
        payload: { subPropIndex, optionGroupIndex, postfixText },
    }: setGroupPostfix
) => {
    return {
        ...state,
        editableProperty: {
            ...state.editableProperty,
            subProperties: insertToIndex(
                state.editableProperty.subProperties,
                subPropIndex,
                {
                    ...state.editableProperty.subProperties[subPropIndex],
                    optionGroups: insertToIndex(
                        state.editableProperty.subProperties[subPropIndex]
                            .optionGroups,
                        optionGroupIndex,
                        {
                            ...state.editableProperty.subProperties[
                                subPropIndex
                            ].optionGroups[optionGroupIndex],
                            postfix: stripDoubleSpaces(postfixText),
                        },
                        true
                    ),
                },
                true
            ),
        },
    }
}

const nonMSReducerObject = {
    SET_MODE: setNonMSModeReducer,
    SET_ERRORS: setNonMSErrorReducer,
    RESET_ERRORS: resetNonMSErrorReducer,
    CREATE_NEW_PROPERTY: createNewPropertyReducer,
    SET_EDIT_PROPERTY: setEditPropertyReducer,
    SET_EDIT_PROPERTY_ID: setEditPropertyID,
    INSERT_PROPERTY: insertPropertyReducer,
    INSERT_OPTION_GROUP: insertPropertyReducer,
    SET_PROPERTIES: setPropertiesReducer,
    CREATE_NEW_SUBPROPERTY: createNewSubPropertyReducer,
    SET_SUBPROPERTIES: setSubPropertiesReducer,
    SET_SUBPROPERTY: setSubPropertyReducer,
    SET_PROPERTY_NAME: setPropertyNameReducer,
    SET_SUBPROPERTY_NAME: setSubPropertyNameReducer,
    SET_OPTIONS: setOptionsReducer,
    SORT_SET_OPTIONS: sortOptionsReducer,
    SET_PRISTINE_NONMS: setPristineNonMSReducer,
    SET_OPTION_ACTIVE: setOptionActiveReducer,
    SET_PREFIX_EDITABLE: setPrefixEditableReducer,
    SET_GROUP_PREFIX: setGroupPrefixReducer,
    SET_GROUP_POSTFIX: setGroupPostfixReducer,
}

const batchNonMSActionsReducer = (
    state: NonMSState,
    action: batchNonMSActions
) => {
    let tempState: NonMSState = { ...state }

    action.payload.forEach((reducer) => {
        if (nonMSReducerObject[reducer.type]) {
            tempState = nonMSReducerObject[reducer.type](
                tempState,
                reducer as any
            ) //this is set as any because some reducer doesn't need an action
        }
    })

    return tempState
}

export const nonMSReducer = (
    state: NonMSState,
    action: NonMSAction | batchNonMSActions
) => {
    if (action.type === 'BATCH_NONMS_ACTIONS') {
        return batchNonMSActionsReducer(state, action)
    } else if (!!nonMSReducerObject[action.type]) {
        return nonMSReducerObject[action.type](
            state,
            action as any //this is set as any because some reducer doesn't need an action
        )
    }
    return state
}
